import { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { authService } from '../services/authService';
import { useNotifications } from '../context/NotificationContext';
import { LoginCredentials, User } from '../types/auth.types';

// Extended auth hook that provides additional functionality beyond the basic context
export function useAuth() {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotifications();

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// Enhanced authentication hook with additional features
export function useAuthExtended() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotifications();
  
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lastActivity, setLastActivity] = useState<Date>(new Date());

  // Enhanced login with additional features
  const loginWithNotification = useCallback(async (credentials: LoginCredentials) => {
    try {
      setIsLoggingIn(true);
      setLoginAttempts(prev => prev + 1);
      
      await auth.login(credentials);
      
      // Success notification
      showSuccess(
        'Welcome back!',
        `Successfully logged in as ${credentials.email}`
      );

      // Redirect to intended page or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
      
      // Reset login attempts on success
      setLoginAttempts(0);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      showError('Login Failed', errorMessage);
      
      // Show warning after multiple failed attempts
      if (loginAttempts >= 2) {
        showWarning(
          'Multiple Login Attempts',
          'Please double-check your credentials or contact support if you continue having issues.'
        );
      }
      
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  }, [auth, navigate, location, showSuccess, showError, showWarning, loginAttempts]);

  // Enhanced logout with confirmation
  const logoutWithConfirmation = useCallback(async (showConfirmation = false) => {
    if (showConfirmation) {
      if (!window.confirm('Are you sure you want to log out?')) {
        return;
      }
    }

    try {
      setIsLoggingOut(true);
      
      await auth.logout();
      
      showSuccess('Logged Out', 'You have been successfully logged out');
      navigate('/login', { replace: true });
      
    } catch (error) {
      showError('Logout Error', 'There was an issue logging out');
    } finally {
      setIsLoggingOut(false);
    }
  }, [auth, navigate, showSuccess, showError]);

  // Auto-logout on inactivity
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const handleActivity = () => {
      setLastActivity(new Date());
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Check for inactivity every minute
    const inactivityTimer = setInterval(() => {
      const now = new Date();
      const inactiveTime = now.getTime() - lastActivity.getTime();
      const maxInactivity = 30 * 60 * 1000; // 30 minutes

      if (inactiveTime > maxInactivity) {
        showWarning(
          'Session Expired',
          'You have been logged out due to inactivity'
        );
        auth.logout();
      } else if (inactiveTime > maxInactivity - 5 * 60 * 1000) {
        // Warn 5 minutes before logout
        showWarning(
          'Session Expiring Soon',
          'Your session will expire soon due to inactivity. Click anywhere to stay logged in.'
        );
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(inactivityTimer);
    };
  }, [auth.isAuthenticated, lastActivity, auth, showWarning]);

  return {
    ...auth,
    loginWithNotification,
    logoutWithConfirmation,
    isLoggingIn,
    isLoggingOut,
    loginAttempts,
    lastActivity,
  };
}

// Role-based access control hook
export function useRoleAccess() {
  const { user } = useAuth();

  const hasRole = useCallback((role: string): boolean => {
    return user?.role === role;
  }, [user]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  }, [user]);

  const canAccess = useCallback((resource: string): boolean => {
    if (!user) return false;

    // Role-based access control mapping
    const rolePermissions: Record<string, string[]> = {
      admin: ['*'], // Full access
      manager: ['dashboard', 'bookings', 'assignments', 'fleet', 'finance', 'config', 'zones', 'promotions'],
      dispatcher: ['dashboard', 'bookings', 'assignments', 'fleet'],
      finance: ['dashboard', 'bookings', 'finance'],
      viewer: ['dashboard'], // Read-only access
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes('*') || userPermissions.includes(resource);
  }, [user]);

  const canEdit = useCallback((resource: string): boolean => {
    if (!user) return false;
    
    // Viewers can't edit anything
    if (user.role === 'viewer') return false;
    
    return canAccess(resource);
  }, [user, canAccess]);

  const canDelete = useCallback((resource: string): boolean => {
    if (!user) return false;
    
    // Only admins and managers can delete
    return ['admin', 'manager'].includes(user.role) && canAccess(resource);
  }, [user, canAccess]);

  // Specific permission checks
  const permissions = {
    // Dashboard
    canViewDashboard: canAccess('dashboard'),
    
    // Bookings
    canViewBookings: canAccess('bookings'),
    canEditBookings: canEdit('bookings'),
    canCancelBookings: hasAnyRole(['admin', 'manager', 'dispatcher']),
    canAssignBookings: hasAnyRole(['admin', 'manager', 'dispatcher']),
    
    // Fleet
    canViewFleet: canAccess('fleet'),
    canEditDrivers: canEdit('fleet'),
    canManageVehicles: canEdit('fleet'),
    canApproveDocuments: hasAnyRole(['admin', 'manager']),
    
    // Finance
    canViewFinance: canAccess('finance'),
    canProcessRefunds: hasAnyRole(['admin', 'manager', 'finance']),
    canViewReports: canAccess('finance'),
    
    // Configuration
    canViewConfig: canAccess('config'),
    canEditConfig: hasAnyRole(['admin', 'manager']),
    canEditPricing: hasRole('admin'),
    
    // System Administration
    canManageUsers: hasRole('admin'),
    canViewAuditLogs: hasAnyRole(['admin', 'manager']),
    canManageSystem: hasRole('admin'),
  };

  return {
    hasRole,
    hasAnyRole,
    canAccess,
    canEdit,
    canDelete,
    permissions,
    userRole: user?.role || null,
    isAdmin: hasRole('admin'),
    isManager: hasRole('manager'),
    isDispatcher: hasRole('dispatcher'),
    isFinance: hasRole('finance'),
    isViewer: hasRole('viewer'),
  };
}

// Session management hook
export function useSessionManager() {
  const { user, accessToken, refreshToken, isAuthenticated } = useAuth();
  const [sessionWarningShown, setSessionWarningShown] = useState(false);

  // Calculate session info
  const getSessionInfo = useCallback(() => {
    if (!user || !accessToken) return null;

    return {
      userId: user.id,
      email: user.email,
      role: user.role,
      isAuthenticated,
      lastLoginAt: user.lastLoginAt,
      hasValidToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
    };
  }, [user, accessToken, refreshToken, isAuthenticated]);

  // Check if session is about to expire
  const isSessionExpiringSoon = useCallback((): boolean => {
    if (!user?.lastLoginAt) return false;

    const loginTime = new Date(user.lastLoginAt).getTime();
    const now = Date.now();
    const sessionDuration = now - loginTime;
    const tokenExpiryTime = 20 * 60 * 1000; // 20 minutes
    const warningThreshold = tokenExpiryTime - 5 * 60 * 1000; // 5 minutes before expiry

    return sessionDuration > warningThreshold;
  }, [user]);

  // Auto-refresh token when needed
  useEffect(() => {
    if (!isAuthenticated || !refreshToken) return;

    const refreshInterval = setInterval(async () => {
      try {
        if (isSessionExpiringSoon() && !sessionWarningShown) {
          setSessionWarningShown(true);
          // The AuthContext will handle the actual token refresh
        }
      } catch (error) {
        console.error('Session check failed:', error);
      }
    }, 60000); // Check every minute

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, refreshToken, isSessionExpiringSoon, sessionWarningShown]);

  return {
    getSessionInfo,
    sessionInfo: getSessionInfo(),
    isSessionExpiringSoon,
    sessionWarningShown,
    setSessionWarningShown,
  };
}

// User profile management hook
export function useUserProfile() {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateProfile = useCallback(async (updates: Partial<User>) => {
    if (!user) return;

    try {
      setIsUpdating(true);
      await updateUser(updates);
      showSuccess('Profile Updated', 'Your profile has been successfully updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Update failed';
      showError('Update Failed', errorMessage);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [user, updateUser, showSuccess, showError]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      setIsUpdating(true);
      await authService.changePassword(currentPassword, newPassword);
      showSuccess('Password Changed', 'Your password has been successfully updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password change failed';
      showError('Password Change Failed', errorMessage);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  }, [showSuccess, showError]);

  const updatePreferences = useCallback(async (preferences: User['preferences']) => {
    if (!user) return;

    await updateProfile({ preferences });
  }, [user, updateProfile]);

  return {
    user,
    updateProfile,
    changePassword,
    updatePreferences,
    isUpdating,
  };
}

// Authentication guard hook for components
export function useAuthGuard(requiredRole?: string | string[], redirectTo = '/login') {
  const { isAuthenticated, loading } = useAuth();
  const { hasRole, hasAnyRole } = useRoleAccess();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      navigate(redirectTo, { 
        state: { from: location },
        replace: true 
      });
      return;
    }

    if (requiredRole) {
      const hasRequiredRole = Array.isArray(requiredRole) 
        ? hasAnyRole(requiredRole)
        : hasRole(requiredRole);

      if (!hasRequiredRole) {
        navigate('/unauthorized', { replace: true });
        return;
      }
    }
  }, [isAuthenticated, loading, requiredRole, hasRole, hasAnyRole, navigate, location, redirectTo]);

  return {
    isAuthenticated,
    loading,
    canAccess: !loading && isAuthenticated && (!requiredRole || 
      (Array.isArray(requiredRole) ? hasAnyRole(requiredRole) : hasRole(requiredRole))
    ),
  };
}

export default useAuth;